import { EventLink, RegistrationStatus } from './types';

/** A distance variant that may be distance- or time-based (24h race etc.). */
type DistanceLike = { distanceKm: number; durationMinutes?: number | null };

/** True if this variant is time-limited (24h/12h race) rather than a fixed distance. */
export function isTimeVariant(d: DistanceLike): boolean {
  return d.durationMinutes != null && d.durationMinutes > 0;
}

/** "24 h", "45 min", "2 h 30 min" — Stunden und Minuten, je nachdem was gesetzt ist. */
export function formatDurationMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/**
 * Zerlegung fürs große Kachel-Layout (Zahl groß, Einheit klein): [value, unit].
 * Ganze Stunden → ["24","h"], reine Minuten → ["45","min"], gemischt → ["2:30","h"].
 */
export function durationChipParts(minutes: number): [string, string] {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return [String(m), 'min'];
  if (m === 0) return [String(h), 'h'];
  return [`${h}:${String(m).padStart(2, '0')}`, 'h'];
}

/**
 * Short one-line label for a variant: the time limit for time variants ("24 h"),
 * otherwise the distance ("150 km"). Used in compact lists/cards.
 */
export function distanceShortLabel(d: DistanceLike): string {
  if (isTimeVariant(d)) return formatDurationMinutes(d.durationMinutes!);
  return `${d.distanceKm} km`;
}

/**
 * Chip-Beschriftungen einer Variantenliste in Anzeige-Reihenfolge: erst die Distanz-Varianten
 * aufsteigend ("100 km"), dann die Zeit-Varianten aufsteigend ("24 h"). Gemeinsame Quelle für
 * die Card-Chips in Web und Mobile.
 */
export function orderedVariantLabels(distances: DistanceLike[]): string[] {
  const distanceVars = distances
    .filter((d) => !isTimeVariant(d) && d.distanceKm > 0)
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .map((d) => `${d.distanceKm} km`);
  const timeVars = distances
    .filter(isTimeVariant)
    .sort((a, b) => (a.durationMinutes as number) - (b.durationMinutes as number))
    .map((d) => formatDurationMinutes(d.durationMinutes as number));
  return [...distanceVars, ...timeVars];
}

/**
 * Macht eine (evtl. schema-lose) URL für `href`/`Linking.openURL` benutzbar.
 *
 * Redaktionell/importiert gepflegte URLs kommen oft ohne Schema an ("www.example.de/",
 * "example.de/anmeldung"). Ohne Schema deutet der Browser sie als *relativen* Pfad —
 * aus /events/123 + "www.example.de/" wird /events/www.example.de/. Fehlt ein Schema,
 * ergänzen wir "https://". Protokoll-relative ("//host") und andere Schemata
 * (mailto:, tel:, http(s):) bleiben unangetastet.
 */
export function toExternalUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed)?.[1]?.toLowerCase();
  if (scheme) {
    // Nur erwartete, ungefährliche Schemata zulassen. Insbesondere KEIN javascript:/data:/vbscript:
    // — sonst wird ein aus einem (anonymen) Vorschlag stammendes href={…} beim Klick als Skript
    // im rad-events.net-Origin ausgeführt (Stored XSS, Token-Diebstahl aus localStorage).
    return ['http', 'https', 'mailto', 'tel'].includes(scheme) ? trimmed : '';
  }
  return `https://${trimmed}`;
}

/**
 * Wie genau ein Anmeldestart bekannt ist. Veranstalter kündigen ihn mal nur grob an
 * („Anmeldestart Dezember 2026"), mal auf die Minute („1.12., 9:00 Uhr" — bei begehrten Terminen
 * entscheidet genau das). Deshalb trägt das Feld ein *abgeschnittenes* ISO-Datum:
 * `2027` (YEAR), `2026-12` (MONTH), `2026-12-01` (DAY) oder `2026-12-01T09:00` (MINUTE).
 * Das Backend leitet daraus Datum, Uhrzeit und Genauigkeit ab und schickt dieselbe Form zurück.
 */
export type RegistrationStartPrecision = 'MINUTE' | 'DAY' | 'MONTH' | 'YEAR';

/** Genauigkeit eines Anmeldestart-Werts. Leer/ungültig → 'DAY' (die Voreinstellung im Formular). */
export function registrationStartPrecision(value?: string | null): RegistrationStartPrecision {
  const raw = (value ?? '').trim();
  if (raw.includes('T')) return 'MINUTE';
  const parts = raw.split('-');
  if (parts.length >= 3 && parts[2]) return 'DAY';
  if (parts.length === 2 && parts[1]) return 'MONTH';
  if (parts.length === 1 && parts[0]) return 'YEAR';
  return 'DAY';
}

/** Der Datumsteil eines Anmeldestart-Werts, ohne Uhrzeit ('2026-12-01T09:00' → '2026-12-01'). */
export function registrationStartDay(value?: string | null): string {
  return (value ?? '').trim().split('T')[0];
}

/** Die Uhrzeit eines Anmeldestart-Werts ('09:00'), oder '' wenn keine gepflegt ist. */
export function registrationStartTime(value?: string | null): string {
  return (value ?? '').trim().split('T')[1] ?? '';
}

// Eigene Liste statt toLocaleDateString: Monatsnamen müssen in App und Web identisch sein,
// und die Intl-Daten sind auf Android (Hermes) nicht überall vollständig.
export const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/**
 * „1. Dezember 2026, 9:00 Uhr", „1. Dezember 2026", „Dezember 2026" oder „2027" — je nachdem, wie
 * genau der Wert ist. Nie genauer als das, was gepflegt wurde.
 */
export function formatRegistrationStart(value?: string | null): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const time = registrationStartTime(raw);
  const [year, month, day] = registrationStartDay(raw).split('-');
  const monthName = MONTH_NAMES_DE[Number(month) - 1];
  if (!monthName) return year;
  if (!day) return `${monthName} ${year}`;
  const date = `${Number(day)}. ${monthName} ${year}`;
  return time ? `${date}, ${time} Uhr` : date;
}

/**
 * Der Anmeldestand, wie ihn Liste und Detailseite zeigen — eine einzige Aussage statt mehrerer
 * konkurrierender Badges.
 *
 * Die Reihenfolge ist die eigentliche Regel: Eine **Absage** übertrumpft alles, danach kommt
 * **ausgebucht** — beides beendet die Anmeldung, egal was im Anmelde-Block steht. Ein Event, das
 * ausgebucht ist und dessen Anmeldung „läuft", wäre für Lesende ein Widerspruch. Erst darunter
 * greift der Anmeldestand selbst.
 *
 * `tone` benennt die Bedeutung, nicht die Farbe — Web und App setzen sie in ihre eigenen Paletten um.
 */
export type RegistrationBadgeTone = 'negative' | 'warning' | 'positive' | 'neutral';

export interface RegistrationBadge {
  /** Versalien — passend zu den bestehenden Status-Badges (Absage/ausgebucht) auf der Event-Seite. */
  short: string;
  /**
   * Beschriftung in Satzschreibung. Der Anmeldestand wird **so** gezeigt: Versalien mit Sperrsatz
   * sind auf Chip-Größe schlecht zu lesen, und der Stand ist eine Auskunft, kein Warnruf.
   */
  label: string;
  /** Ausgeschriebene Form für Tooltip und Vorlese-Programme. */
  long: string;
  tone: RegistrationBadgeTone;
}

export function registrationBadge(event: {
  cancelled?: boolean;
  fullyBooked?: boolean;
  registrationStatus?: RegistrationStatus;
}): RegistrationBadge | null {
  if (event.cancelled) return { short: 'ABGESAGT', label: 'Abgesagt', long: 'Abgesagt', tone: 'negative' };
  if (event.fullyBooked) {
    return { short: 'AUSGEBUCHT', label: 'Ausgebucht', long: 'Ausgebucht — keine Anmeldung mehr möglich', tone: 'warning' };
  }
  switch (event.registrationStatus) {
    case 'OPEN':
      return { short: 'ANMELDUNG OFFEN', label: 'Anmeldung offen', long: 'Anmeldung geöffnet', tone: 'positive' };
    case 'OPENING_SOON':
      // Bewusst ohne Datum: dass es bald so weit ist, ist die Aussage — der Termin selbst bleibt
      // eine interne Angabe.
      return { short: 'ANMELDUNG BALD', label: 'Anmeldung bald', long: 'Anmeldung öffnet in Kürze', tone: 'neutral' };
    case 'NOT_YET_OPEN':
      // Ein Termin in Monaten ist für Lesende keine Information, sondern Rauschen — dann lieber
      // gar nichts sagen. Der Anmelde-Button bleibt trotzdem weg, siehe canRegisterNow.
      return null;
    case 'CLOSED':
      // Bewusst der ruhige Chip und nicht das laute Rot von „ausgebucht"/„abgesagt": die Anmeldung
      // ist zu, die Veranstaltung findet aber statt — oft ist vor Ort noch etwas möglich.
      return { short: 'ANMELDUNG ZU', label: 'Anmeldung geschlossen', long: 'Anmeldung geschlossen', tone: 'neutral' };
    case 'ONSITE_ONLY':
      return { short: 'ANMELDUNG VOR ORT', label: 'Anmeldung vor Ort', long: 'Anmeldung nur vor Ort', tone: 'neutral' };
    default:
      return null;
  }
}

/**
 * Der Anmeldestand auf der **Event-Seite**. Dort zeigt auch eine Anmeldung, die erst in Monaten
 * öffnet, einen Chip, im selben Stil wie „Anmeldung bald“: auf der Seite ist das die Antwort auf
 * „kann ich mich anmelden?“, und daneben steht die Glocke. In der Liste bleibt es bei
 * [registrationBadge], dort wäre ein Termin in Monaten Rauschen.
 */
export function registrationDetailBadge(event: {
  cancelled?: boolean;
  fullyBooked?: boolean;
  registrationStatus?: RegistrationStatus;
}): RegistrationBadge | null {
  const badge = registrationBadge(event);
  if (badge || event.registrationStatus !== 'NOT_YET_OPEN') return badge;
  // Derselbe Chip wie bei OPENING_SOON (Phil, 01.10.2026): ob in drei Wochen oder drei Monaten,
  // für Lesende ist es „bald“, und die Glocke daneben erledigt den Rest.
  return registrationBadge({ ...event, registrationStatus: 'OPENING_SOON' });
}

/**
 * Kann man sich **jetzt** anmelden? Steuert, ob der Anmelde-Button überhaupt erscheint.
 *
 * Bewusst getrennt von [registrationBadge]: ein Termin in Monaten bekommt kein Badge (er wäre
 * Rauschen), der Button muss trotzdem weg — er führte auf ein Formular, das es noch nicht gibt.
 * Ohne jede Angabe (`UNKNOWN`) bleibt der Button: das ist der Bestand tausender Events, deren
 * Anmelde-Link seit jeher funktioniert.
 */
export function canRegisterNow(event: {
  cancelled?: boolean;
  fullyBooked?: boolean;
  registrationStatus?: RegistrationStatus;
}): boolean {
  if (event.cancelled || event.fullyBooked) return false;
  return !event.registrationStatus || event.registrationStatus === 'UNKNOWN' || event.registrationStatus === 'OPEN';
}

/**
 * Soll der Anmelde-Link überhaupt angeboten werden?
 *
 * Weiter gefasst als [canRegisterNow]: Bei **geschlossener** Anmeldung führt der Link zwar auf
 * kein offenes Formular mehr, aber genau dort steht in aller Regel, ob am Veranstaltungstag noch
 * eine Nachmeldung vor Ort möglich ist — die Seite wegzunehmen nähme Lesenden diese Auskunft.
 * Bei einer noch nicht geöffneten Anmeldung bleibt er dagegen weg: dort ist nichts zu holen.
 */
export function showsRegistrationLink(event: {
  cancelled?: boolean;
  fullyBooked?: boolean;
  registrationStatus?: RegistrationStatus;
  eventDate?: string;
  endDate?: string | null;
  permanent?: boolean;
}): boolean {
  // Absage und „ausgebucht" gewinnen auch hier: bei einem abgesagten Event hilft keine Auskunft
  // zur Nachmeldung mehr, und die Ausnahme unten würde sie sonst wieder hereinholen.
  if (event.cancelled || event.fullyBooked) return false;
  // Gelaufen: dort ist nichts mehr anzumelden, es bleibt der Weg zum Veranstalter (Phil,
  // 05.11.2026). Permanente Events tragen nur einen Platzhalter-Termin in der Vergangenheit.
  if (event.eventDate && !event.permanent && daysUntilEvent(event.eventDate, event.endDate) < 0) return false;
  return canRegisterNow(event) || event.registrationStatus === 'CLOSED';
}

/** Die erste öffentliche Anmelde-URL eines Events; '' wenn keine hinterlegt ist. */
export function registrationLinkOf(links: EventLink[] | undefined | null): string {
  const found = (links ?? []).find(l => l.type === 'REGISTRATION' && l.visibility !== 'INTERNAL');
  return found?.url ?? '';
}

/**
 * Setzt die Anmelde-URL in einer Linkliste, ohne eine zweite Quelle der Wahrheit aufzumachen:
 * der Anmelde-Link *ist* der `REGISTRATION`-Link des Events. Leerer Wert entfernt ihn.
 */
export function withRegistrationLink(links: EventLink[] | undefined | null, url: string): EventLink[] {
  const rest = (links ?? []).filter(l => !(l.type === 'REGISTRATION' && l.visibility !== 'INTERNAL'));
  const trimmed = url.trim();
  if (!trimmed) return rest;
  const existing = (links ?? []).find(l => l.type === 'REGISTRATION' && l.visibility !== 'INTERNAL');
  return [...rest, { ...existing, url: trimmed, type: 'REGISTRATION', visibility: 'PUBLIC' }];
}

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Parse a YYYY-MM-DD (or ISO) string into a local-midnight Date, avoiding timezone drift. */
export function parseLocalDay(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * Tage bis zum Event: 0 = heute, 1 = morgen, negativ = vorbei.
 * Ein bereits gestartetes mehrtägiges Event (Start vorbei, endDate heute oder später)
 * zählt weiterhin als "heute" (0) — sonst verliert es Countdown-Pille und Wetter,
 * obwohl es noch läuft.
 */
export function daysUntilEvent(eventDate: string, endDate?: string | null, now: Date = new Date()): number {
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  const days = Math.round((parseLocalDay(eventDate).getTime() - today.getTime()) / 86400000);
  if (days < 0 && endDate) {
    const daysToEnd = Math.round((parseLocalDay(endDate).getTime() - today.getTime()) / 86400000);
    if (daysToEnd >= 0) return 0;
  }
  return days;
}

/**
 * Powers the "Tour de France" house promo banner (web leaderboard + mobile card).
 *
 * Finds the upcoming men's Tour de France among the pro-race list and derives the
 * countdown label used in "Die Tour de France startet {countdown}". Returns `null`
 * once the race has started (banner should disappear) or when no such race exists,
 * so the campaign never has to be hardcoded or manually retired.
 */
export interface TdfPromo<T> {
  /** The matched Tour de France pro-race (use for navigation: slug on web, id on mobile). */
  race: T;
  /** Countdown label: 'heute' | 'morgen' | 'in N Tagen'. */
  countdown: string;
  /** Whole days until the start (0 = today). */
  days: number;
}

export function getTourDeFrancePromo<
  T extends { name: string; slug: string; startDate: string }
>(races: T[], now: Date = new Date()): TdfPromo<T> | null {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isMensTdf = (r: T) =>
    /tour de france/i.test(r.name) && !/femmes/i.test(`${r.name} ${r.slug}`);

  const upcoming = races
    .filter(isMensTdf)
    .map((race) => ({ race, start: parseLocalDay(race.startDate) }))
    .filter((x) => x.start.getTime() >= today.getTime())
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const chosen = upcoming[0];
  if (!chosen) return null;

  const days = Math.round((chosen.start.getTime() - today.getTime()) / 86400000);
  const countdown = days === 0 ? 'heute' : days === 1 ? 'morgen' : `in ${days} Tagen`;
  return { race: chosen.race, countdown, days };
}

/**
 * Segment of a news paragraph: plain text, or a link imported from the old site
 * (stored inline as `[text](url)` in NewsPost.content).
 */
export interface NewsContentSegment {
  text: string;
  /** Set if this segment is a hyperlink. */
  url?: string;
  bold?: boolean;
  italic?: boolean;
}

/**
 * Inline-Marker eines Absatzes.
 *
 * Bewusst KEIN `_` als Kursiv-Marker: Bestandsartikel enthalten URLs und Bezeichner mit
 * Unterstrichen, und `_` als Delimiter zerlegt die — aus `https://example.com/my_event_page`
 * wurde `.../my` + kursiv(event) + `page`, die URL war auf der Seite zerstört. Der Editor
 * serialisiert Kursiv ohnehin als `*`.
 *
 * `*` verlangt zusätzlich nicht-leerzeichen-angrenzenden Inhalt (`*so*`, nicht `30 * 2`),
 * damit Mal-Zeichen im Fließtext keine Kursivspanne mehr aufziehen.
 *
 * `<https://…>` ist die Kurzform, die der Editor schreibt, wenn der Linktext genau die Adresse ist.
 */
const INLINE_RE =
  /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)|<(https?:\/\/[^\s<>]+|mailto:[^\s<>]+)>|\*\*([^\n]+?)\*\*|\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*/g;

/** Markdown-Escapes des Editors (prosemirror-markdown escapt `* _ [ ] \` ~`) wieder entfernen. */
const unescapeMd = (s: string): string => s.replace(/\\([\\*_[\]()`~#>-])/g, '$1');

type InlineStyle = { bold?: boolean; italic?: boolean };

function parseInline(text: string, style: InlineStyle = {}): NewsContentSegment[] {
  const segments: NewsContentSegment[] = [];
  const push = (raw: string, extra: InlineStyle & { url?: string } = {}) => {
    const t = unescapeMd(raw);
    if (t.length > 0) segments.push({ text: t, ...style, ...extra });
  };

  const re = new RegExp(INLINE_RE.source, 'g');
  let last = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) push(text.slice(last, m.index));

    if (m[1] !== undefined) {
      // Linktext darf selbst **fett**/*kursiv* tragen — rekursiv auflösen und die URL an jedes
      // Teilstück hängen. Sonst landete `[**fetter Link**](url)` mit sichtbaren Sternchen auf der Seite.
      for (const seg of parseInline(m[1], style)) segments.push({ ...seg, url: m[2] });
    } else if (m[3] !== undefined) {
      // Adresse wörtlich übernehmen: sie ist Text und Ziel zugleich, ein Sternchen darin bleibt stehen.
      segments.push({ text: m[3], ...style, url: m[3] });
    } else if (m[4] !== undefined) {
      // Ein Link INNERHALB von **…** muss ebenfalls Link bleiben, nicht als roher Markdown-Text
      // erscheinen (`**[Link](url)**` war genau dieser Fall).
      segments.push(...parseInline(m[4], { ...style, bold: true }));
    } else {
      segments.push(...parseInline(m[5], { ...style, italic: true }));
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) push(text.slice(last));
  return segments;
}

/** Splits a news paragraph into text/link segments (links are stored as `[text](url)`). */
export function parseNewsParagraph(paragraph: string): NewsContentSegment[] {
  return parseInline(paragraph);
}

/**
 * A block of news content. `content` is stored as a small Markdown subset that the
 * admin editor round-trips: paragraphs, `##`/`###` headings, `-`/`1.` lists, `>` quotes.
 */
export type NewsBlock =
  | { type: 'paragraph'; segments: NewsContentSegment[] }
  | { type: 'heading'; level: 2 | 3; segments: NewsContentSegment[] }
  | { type: 'quote'; segments: NewsContentSegment[] }
  | { type: 'list'; ordered: boolean; items: NewsContentSegment[][] };

/** Max. Länge eines aus dem Fließtext abgeleiteten Teasers (Zeichen). */
const DERIVED_TEASER_MAX = 200;

const segmentsText = (segments: NewsContentSegment[]): string => segments.map(s => s.text).join('');

/**
 * Leitet aus dem Fließtext einen Teaser ab, wenn keiner gepflegt ist: der erste Absatz
 * (bzw. das erste Zitat/die erste Liste) als reiner Text — Überschriften werden übersprungen —,
 * an der Wortgrenze auf {@link DERIVED_TEASER_MAX} gekürzt und mit „…" abgeschlossen.
 * Markdown-Marker sind über {@link parseNewsContent} bereits entfernt.
 */
export function deriveNewsTeaser(content: string): string {
  let text = '';
  for (const block of parseNewsContent(content || '')) {
    if (block.type === 'heading') continue; // Überschriften sind kein Teaser-Satz
    text = block.type === 'list' ? block.items.map(segmentsText).join(' · ') : segmentsText(block.segments);
    if (text.trim()) break;
  }
  text = text.trim().replace(/\s+/g, ' ');
  if (text.length <= DERIVED_TEASER_MAX) return text;
  const cut = text.slice(0, DERIVED_TEASER_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  // kein String.trimEnd(): das shared-tsconfig-Target (ES2018) kennt es nicht
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/\s+$/, '') + '…';
}

/** Gepflegter Teaser, sonst aus dem Fließtext abgeleitet (nur wo `content` vorliegt). */
export function newsTeaser(post: { teaser?: string | null; content?: string | null }): string {
  const t = post.teaser?.trim();
  if (t) return t;
  return post.content ? deriveNewsTeaser(post.content) : '';
}

/**
 * Parses a news article body into renderable blocks. Plain text without any Markdown
 * markers still yields one paragraph per blank-line-separated block, so articles written
 * before the editor existed render unchanged.
 */
export function parseNewsContent(content: string): NewsBlock[] {
  const blocks: NewsBlock[] = [];

  for (const raw of content.split(/\n\s*\n/)) {
    const chunk = raw.trim();
    if (!chunk) continue;

    // Listen dürfen mehrzeilig sein; alle anderen Blöcke behandeln wir als eine Einheit,
    // damit ein weicher Zeilenumbruch im Absatz keinen neuen Block erzeugt.
    const lines = chunk.split('\n').map(l => l.trim()).filter(Boolean);
    const isList = lines.every(l => /^([-*]|\d+\.)\s+/.test(l));

    if (isList) {
      const ordered = /^\d+\.\s+/.test(lines[0]);
      blocks.push({
        type: 'list',
        ordered,
        items: lines.map(l => parseNewsParagraph(l.replace(/^([-*]|\d+\.)\s+/, ''))),
      });
      continue;
    }

    const heading = chunk.match(/^(#{2,3})\s+(.*)$/s);
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length === 2 ? 2 : 3,
        segments: parseNewsParagraph(heading[2].replace(/\n\s*/g, ' ')),
      });
      continue;
    }

    if (lines.every(l => l.startsWith('>'))) {
      const text = lines.map(l => l.replace(/^>\s?/, '')).join(' ');
      blocks.push({ type: 'quote', segments: parseNewsParagraph(text) });
      continue;
    }

    blocks.push({ type: 'paragraph', segments: parseNewsParagraph(lines.join(' ')) });
  }

  return blocks;
}

/**
 * Gilt der Umkreis-Filter gerade — oder setzt ihn eine Textsuche aus?
 *
 * Wer einen Namen eintippt, meint genau dieses Event, auch wenn es 500 km entfernt startet. Der
 * Standort bleibt dabei gesetzt und greift wieder, sobald das Suchfeld leer ist. Web und App
 * teilen sich diese Regel, weil sonst die eine Plattform Treffer zeigt, die die andere verschweigt.
 */
export function radiusFilterApplies(
  lat: number | null | undefined,
  lng: number | null | undefined,
  radiusKm: number | null | undefined,
  searchQuery: string | null | undefined,
): boolean {
  // Ohne Koordinaten schraenkt eine Weite nichts ein — dann gibt es auch nichts auszusetzen.
  return lat != null && lng != null && radiusKm != null && !searchQuery;
}

/**
 * Umkreis ist gesetzt, wirkt aber gerade nicht. Die Oberflaeche muss das sagen: ein Treffer aus
 * Tirol neben einem Chip „100 km Umkreis" sieht sonst nach einem kaputten Filter aus.
 */
export function radiusFilterPaused(
  lat: number | null | undefined,
  lng: number | null | undefined,
  radiusKm: number | null | undefined,
  searchQuery: string | null | undefined,
): boolean {
  return lat != null && lng != null && radiusKm != null && !!searchQuery;
}

/**
 * Vergleichsform einer Link-URL: Schema, „www.", Groß-/Kleinschreibung des Hosts, Schrägstrich
 * am Ende und Fragment zählen nicht — dieselbe Regel wie `canonicalLinkUrl` im Backend
 * (`EventLink.kt`). Query-Parameter bleiben: zwei PDFs hinter `?name=` sind zwei Dateien.
 * Beide Seiten müssen gleich rechnen, sonst markiert die Prüfansicht einen Link als „neu",
 * den die Freigabe dann gar nicht anlegt.
 */
export function canonicalLinkUrl(url: string): string {
  const s = url.trim().replace(/^https?:\/\//i, '').split('#')[0];
  const slash = s.indexOf('/');
  const host = (slash < 0 ? s : s.substring(0, slash)).toLowerCase().replace(/^www\./, '');
  const rest = slash < 0 ? '' : s.substring(slash);
  return (host + rest).replace(/\/+$/, '');
}
